import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Fan, Thermometer, Wrench, Flame, Shield, Zap, ArrowRight, Check, Phone } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  fan: Fan,
  thermometer: Thermometer,
  wrench: Wrench,
  flame: Flame,
  shield: Shield,
  zap: Zap,
};

const serviceImages = {
  "ac-installation": "https://customer-assets.emergentagent.com/job_virgils-hvac/artifacts/41tm31ge_IMG_6255.jpeg",
  "heating-installation": "https://customer-assets.emergentagent.com/job_virgils-hvac/artifacts/qw9vt394_IMG_0643.jpeg",
  "ac-repair": "https://customer-assets.emergentagent.com/job_virgils-hvac/artifacts/5gr5ktyc_IMG_7096.png",
  "heating-repair": "https://customer-assets.emergentagent.com/job_virgils-hvac/artifacts/21zkdbif_IMG_6258.jpeg",
  "maintenance": "https://customer-assets.emergentagent.com/job_virgils-hvac/artifacts/6tyqysby_IMG_6254.jpeg",
  "emergency": "https://customer-assets.emergentagent.com/job_94a568bf-da54-41b2-bd7f-4fba373edc52/artifacts/6qcy4bz6_33519FCC-DE6B-474A-8439-B32571D65842.png",
  "mini-split": "https://customer-assets.emergentagent.com/job_virgils-hvac/artifacts/mjkqkrva_IMG_6261.jpeg",
};

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);

  useEffect(() => {
    document.title = "HVAC Services | AC, Heating, Mini Splits | Virgil's Heating & Air";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", "Professional HVAC services in Greater Sacramento: AC installation, heating repair, mini split heat pumps, furnace installation, and 24/7 emergency services. Licensed & insured.");
    }
  }, []);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
        if (response.data.length > 0) {
          setSelectedService(response.data[0]);
        }
      } catch (e) {
        console.error("Error fetching services:", e);
      }
    };
    fetchServices();
  }, []);

  return (
    <div className="pt-36 min-h-screen bg-[#050505]" data-testid="services-page">
      {/* Hero */}
      <section className="relative py-24 overflow-hidden bg-[#0a0a0a]" data-testid="services-hero">
        <div
          className="absolute inset-0 bg-cover bg-center md:bg-right opacity-100 img-sharp"
          style={{ backgroundImage: "url(https://customer-assets.emergentagent.com/job_94a568bf-da54-41b2-bd7f-4fba373edc52/artifacts/mc2l076r_619075B9-8826-49DE-8319-87407FC90ADA.png)" }}
          data-testid="services-hero-bg"
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/80 via-[#050505]/25 to-transparent"></div>
        <div className="container-custom relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <div className="max-w-2xl">
              <p className="font-mono text-[#5b93ff] text-sm uppercase tracking-wider mb-2 font-semibold">Our Services</p>
              <h1 className="font-heading font-black text-4xl md:text-5xl lg:text-6xl gradient-text mb-4">
                PROFESSIONAL HVAC SERVICES
              </h1>
              <p className="text-gray-300 text-lg max-w-2xl">
                From installation to repair, our certified technicians deliver expert solutions 
                for all your heating and cooling needs.
              </p>
            </div>
            <a href="tel:7073843409" className="shrink-0">
              <Button className="btn-urgent text-lg px-8 py-6">
                <Phone className="mr-2 w-5 h-5" />
                707-384-3409
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding" data-testid="services-grid">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Service List */}
            <div className="lg:col-span-1 space-y-4" data-testid="service-list">
              {services.map((service) => {
                const IconComponent = iconMap[service.icon] || Fan;
                const isSelected = selectedService?.id === service.id;
                return (
                  <button
                    key={service.id}
                    onClick={() => setSelectedService(service)}
                    className={`w-full text-left p-6 rounded-xl border-2 transition-all duration-300 ${
                      isSelected 
                        ? 'bg-gradient-to-r from-[#0066CC] to-[#1E40AF] border-[#0066CC] shadow-lg' 
                        : 'bg-[#101014] border-white/10 hover:border-[#0066CC] hover:shadow-md'
                    }`}
                    data-testid={`service-tab-${service.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-xl ${isSelected ? 'bg-white/20' : 'bg-[#1E6BFF]/10'}`}>
                        <IconComponent className={`w-6 h-6 ${isSelected ? 'text-white' : 'text-[#5b93ff]'}`} />
                      </div>
                      <div>
                        <h3 className={`font-heading font-bold text-lg ${isSelected ? 'text-white' : 'text-white'}`}>
                          {service.name}
                        </h3>
                        <p className={`text-sm ${isSelected ? 'text-white/70' : 'text-gray-400'}`}>
                          {service.features.length} features
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Service Detail */}
            {selectedService && (
              <div className="lg:col-span-2" data-testid="service-detail">
                <div className="bg-[#101014] border-2 border-white/10 rounded-2xl overflow-hidden shadow-lg">
                  {/* Service Content */}
                  <div className="p-8">
                    <span className="font-mono text-xs text-[#5b93ff] uppercase tracking-wider bg-[#1E6BFF]/10 px-3 py-1 rounded-full">Service</span>
                    <h2 className="font-heading font-black text-3xl md:text-4xl text-white mt-3 mb-6">
                      {selectedService.name}
                    </h2>
                    <p className="text-gray-300 text-lg mb-8">{selectedService.description}</p>

                    <h4 className="font-heading font-bold text-xl text-white mb-4">What's Included:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                      {selectedService.features.map((feature, index) => (
                        <div 
                          key={index} 
                          className="flex items-center gap-3 bg-white/5 p-4 rounded-xl"
                          data-testid={`feature-${index}`}
                        >
                          <div className="p-2 bg-green-100 rounded-lg">
                            <Check className="w-5 h-5 text-green-600" />
                          </div>
                          <span className="text-gray-300 font-medium">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <div className="flex flex-wrap gap-4">
                      <Link to="/booking">
                        <Button className="btn-primary" data-testid="schedule-service-btn">
                          Schedule This Service
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                      </Link>
                      <a 
                        href="https://app.gethearth.com/partners/air-repair-heating-air" 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        <Button variant="outline" className="btn-outline" data-testid="apply-financing-btn">
                          Apply for Financing
                        </Button>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* All Services Cards */}
      <section className="section-padding bg-gradient-to-br from-[#0a0a12] to-[#0a0a0a]" data-testid="all-services-cards">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-responsive-section gradient-text">
              ALL SERVICES AT A GLANCE
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon] || Fan;
              return (
                <div
                  key={service.id}
                  className="group bg-[#101014] border-2 border-white/10 hover:border-[#0066CC] rounded-2xl transition-all duration-300 overflow-hidden hover:shadow-xl"
                  data-testid={`service-card-full-${service.id}`}
                >
                  <div className="relative h-48">
                    <img
                      src={serviceImages[service.id] || serviceImages["ac-installation"]}
                      alt={service.name}
                      className="w-full h-full object-cover img-sharp group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1E293B] via-transparent to-transparent opacity-60"></div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="icon-container p-3 rounded-xl group-hover:scale-110 transition-transform">
                        <IconComponent className="w-6 h-6 text-[#5b93ff]" />
                      </div>
                      <h3 className="font-heading font-bold text-xl text-white">{service.name}</h3>
                    </div>
                    <p className="text-gray-300 text-sm mb-4 line-clamp-2">{service.description}</p>
                    <Link to="/booking">
                      <Button variant="outline" className="btn-outline w-full">
                        Book Now
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
